/**
 * File: SearchResultsFeedback.tsx
 * Description: Feedback component for search results that allows users to rate and reorder results
 * Author: System
 * Created: 2025-09-30
 * Last Modified: 2025-09-30
 */

import {
  HandThumbUpIcon,
  HandThumbDownIcon,
  ArrowUpIcon,
  ArrowDownIcon,
  ChatBubbleLeftRightIcon,
} from '@heroicons/react/24/outline';
import {
  HandThumbUpIcon as HandThumbUpSolidIcon,
  HandThumbDownIcon as HandThumbDownSolidIcon,
} from '@heroicons/react/24/solid';
import React, { useState } from 'react';

import { cn } from '../../shared/utils';

export interface SearchResultItem {
  id: string;
  name: string;
  source?: 'wiki' | 'guidelines' | 'user_docs'; // Source of the search result
  relevanceScore?: number;
  description?: string;
  summary?: string;
  // Additional metadata from SearchResultDocument
  category?: string;
  size?: number;
  lastModified?: Date;
  author?: string;
  tags?: string[];
  path?: string;
  wiki_path?: string;
  // Chunk data
  chunks?: any[];
  // Search metadata from backend
  searchMetadata?: {
    searchScore?: number;
    rerankerScore?: number;
    searchHighlights?: any;
    searchCaptions?: any;
  };
}

const HIGH_RELEVANCE_LABEL = 'High Relevance';
const MEDIUM_RELEVANCE_LABEL = 'Medium Relevance';
const LOW_RELEVANCE_LABEL = 'Low Relevance';

const HIGH_RELEVANCE_CLASS = 'bg-green-100 text-green-700';
const MEDIUM_RELEVANCE_CLASS = 'bg-amber-100 text-amber-700';
const LOW_RELEVANCE_CLASS = 'bg-gray-100 text-gray-700';

interface RelevanceBadgeData {
  label: string;
  className: string;
}

const getRelevanceBadgeData = (
  index: number,
  total: number,
): RelevanceBadgeData => {
  if (total <= 2) {
    return {
      label: HIGH_RELEVANCE_LABEL,
      className: HIGH_RELEVANCE_CLASS,
    };
  }

  const percentile = (index + 1) / total;

  if (percentile <= 0.33) {
    return {
      label: HIGH_RELEVANCE_LABEL,
      className: HIGH_RELEVANCE_CLASS,
    };
  }

  if (percentile <= 0.66) {
    return {
      label: MEDIUM_RELEVANCE_LABEL,
      className: MEDIUM_RELEVANCE_CLASS,
    };
  }

  return {
    label: LOW_RELEVANCE_LABEL,
    className: LOW_RELEVANCE_CLASS,
  };
};

export interface SearchResultFeedbackData {
  resultId: string;
  feedback: 'up' | 'down' | null;
  comment: string;
  // Add all available result details
  resultName?: string;
  relevanceScore?: number;
  source?: string; // Source of the search result (wiki, guidelines, etc.)
  description?: string;
  summary?: string;
  // Additional metadata from search items
  category?: string;
  size?: number;
  lastModified?: Date;
  author?: string;
  tags?: string[];
  path?: string;
  // Chunk metadata
  chunkMetadata?: {
    chunk_id?: string;
    document_id?: string;
    chunk_type?: string;
    chunk_order?: number;
    keywords?: string[];
    sector?: string;
    url?: string;
    change_date?: string;
  };
  // Search metadata from backend
  searchMetadata?: {
    searchScore?: number;
    rerankerScore?: number;
    searchHighlights?: any;
    searchCaptions?: any;
  };
}

interface SearchResultsFeedbackPopupProps {
  isVisible: boolean;
  onClose: () => void;
  onSubmit: (_feedbackData: SearchResultFeedbackData[], _reorderedResults: SearchResultItem[], _includeContext: boolean, _includeUserName: boolean, _searchQuery?: string) => void;
  searchResults: SearchResultItem[];
  searchQuery?: string; // Add search query prop
}

const SearchResultsFeedbackPopup: React.FC<SearchResultsFeedbackPopupProps> = ({
  isVisible,
  onClose,
  onSubmit,
  searchResults,
  searchQuery,
}) => {
  const [reorderedResults, setReorderedResults] = useState<SearchResultItem[]>(searchResults);
  const [feedbackData, setFeedbackData] = useState<Record<string, SearchResultFeedbackData>>({});
  const [includeContext, setIncludeContext] = useState(true);
  const [includeUserName, setIncludeUserName] = useState(true); // Make username checkbox on by default

  // Initialize feedback data when results change
  React.useEffect(() => {
    setReorderedResults(searchResults);
    const initialFeedback: Record<string, SearchResultFeedbackData> = {};
    searchResults.forEach(result => {
      // Extract chunk metadata from the first chunk if available
      const firstChunk = result.chunks?.[0];
      const chunkMetadata = firstChunk ? {
        chunk_id: firstChunk.chunk_id,
        document_id: firstChunk.document_id,
        chunk_type: firstChunk.chunk_type,
        chunk_order: firstChunk.chunk_order,
        keywords: firstChunk.keywords,
        sector: firstChunk.sector,
        url: firstChunk.url,
        change_date: firstChunk.change_date,
      } : undefined;

      initialFeedback[result.id] = {
        resultId: result.id,
        feedback: null,
        comment: '',
        // Include all available result details
        resultName: result.name,
        relevanceScore: result.relevanceScore,
        source: result.source,
        description: result.description,
        summary: result.summary,
        // Additional metadata from search items
        category: result.category,
        size: result.size,
        lastModified: result.lastModified,
        author: result.author,
        tags: result.tags,
        path: result.path,
        // Chunk metadata
        chunkMetadata,
        // Search metadata from backend
        searchMetadata: result.searchMetadata,
      };
    });
    setFeedbackData(initialFeedback);
  }, [searchResults]);

  const handleFeedbackClick = (resultId: string, type: 'up' | 'down') => {
    setFeedbackData(prev => ({
      ...prev,
      [resultId]: {
        ...prev[resultId],
        feedback: prev[resultId]?.feedback === type ? null : type,
      },
    }));
  };

  const handleCommentChange = (resultId: string, comment: string) => {
    setFeedbackData(prev => ({
      ...prev,
      [resultId]: {
        ...prev[resultId],
        comment,
      },
    }));
  };

  const moveResult = (index: number, direction: 'up' | 'down') => {
    const newResults = [...reorderedResults];
    const targetIndex = direction === 'up' ? index - 1 : index + 1;
    
    if (targetIndex >= 0 && targetIndex < newResults.length) {
      [newResults[index], newResults[targetIndex]] = [newResults[targetIndex], newResults[index]];
      setReorderedResults(newResults);
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const feedbackArray = Object.values(feedbackData).filter(data => 
      data.feedback !== null || data.comment.trim() !== '',
    );
    onSubmit(feedbackArray, reorderedResults, includeContext, includeUserName, searchQuery);
    onClose();
  };

  const handleCancel = () => {
    setReorderedResults(searchResults);
    const resetFeedback: Record<string, SearchResultFeedbackData> = {};
    searchResults.forEach(result => {
      resetFeedback[result.id] = {
        resultId: result.id,
        feedback: null,
        comment: '',
      };
    });
    setFeedbackData(resetFeedback);
    setIncludeContext(true);
    setIncludeUserName(true);
    onClose();
  };

  if (!isVisible) {return null;}

  return (
    <>
      {/* Backdrop */}
      <button 
        className="fixed inset-0 z-[9998] bg-black/50 cursor-pointer border-none"
        onClick={handleCancel}
        onKeyDown={(e) => e.key === 'Escape' && handleCancel()}
        aria-label="Close search results feedback"
        type="button"
      />
      
      {/* Popup Content */}
      <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4">
        <div className="bg-white rounded-lg shadow-xl border border-gray-300 w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
          {/* Header */}
          <div className="p-6 border-b border-gray-300 flex-shrink-0 bg-white">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center">
                <ChatBubbleLeftRightIcon className="w-4 h-4 text-blue-600" />
              </div>
              <div>
                <h3 className="font-semibold text-lg text-gray-900">
                  Search Results Feedback
                </h3>
                <p className="text-gray-600 text-sm">
                  Rate the relevance of search results and reorder them to help improve our search algorithm
                </p>
              </div>
            </div>
          </div>
          
          <form onSubmit={handleSubmit} className="flex flex-col flex-1 min-h-0">
            {/* Results List */}
            <div className="flex-1 overflow-y-auto p-6 space-y-4">
              {reorderedResults.map((result, index) => {
                const feedback = feedbackData[result.id];
                const relevanceBadge = getRelevanceBadgeData(
                  index,
                  reorderedResults.length,
                );
                return (
                  <div key={result.id} className="border border-gray-300 rounded-lg p-4 bg-white">
                    {/* Result Header */}
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <h4 className="text-sm font-medium text-gray-900 truncate">
                            {result.name}
                          </h4>
                          {result.source && (
                            <span className={cn(
                              'px-2 py-0.5 text-xs font-medium rounded-full',
                              result.source === 'wiki' 
                                ? 'bg-blue-100 text-blue-700' 
                                : result.source === 'guidelines'
                                  ? 'bg-purple-100 text-purple-700'
                                  : 'bg-green-100 text-green-700',
                            )}>
                              {result.source === 'wiki' ? 'SAHLD' : result.source === 'guidelines' ? 'Guidelines' : 'User Docs'}
                            </span>
                          )}
                          {result.relevanceScore !== undefined && (
                            <span
                              className={cn(
                                'px-2 py-0.5 text-xs rounded-full font-medium',
                                relevanceBadge.className,
                              )}
                            >
                              {relevanceBadge.label}
                            </span>
                          )}
                        </div>
                        {result.source && (
                          <p className="text-xs text-gray-600 mb-1">
                            Source: {result.source === 'wiki' ? 'SAHLD' : result.source === 'guidelines' ? 'Guidelines' : 'User Documents'}
                          </p>
                        )}
                        {(result.description || result.summary) && (
                          <p className="text-xs text-gray-500 line-clamp-2">
                            {result.description || result.summary}
                          </p>
                        )}
                      </div>
                      
                      {/* Reorder Controls */}
                      <div className="flex flex-col gap-1 ml-4">
                        <button
                          type="button"
                          onClick={() => moveResult(index, 'up')}
                          disabled={index === 0}
                          className="px-2 py-1 text-xs border border-gray-300 rounded bg-white hover:bg-gray-50 disabled:opacity-30 disabled:cursor-not-allowed"
                          title="Move up"
                        >
                          <ArrowUpIcon className="w-3 h-3" />
                        </button>
                        <button
                          type="button"
                          onClick={() => moveResult(index, 'down')}
                          disabled={index === reorderedResults.length - 1}
                          className="px-2 py-1 text-xs border border-gray-300 rounded bg-white hover:bg-gray-50 disabled:opacity-30 disabled:cursor-not-allowed"
                          title="Move down"
                        >
                          <ArrowDownIcon className="w-3 h-3" />
                        </button>
                      </div>
                    </div>
                    
                    {/* Feedback Controls */}
                    <div className="space-y-3">
                      {/* Thumbs Up/Down */}
                      <div className="flex items-center gap-2">
                        <span className="text-xs font-medium text-gray-700 mr-2">Relevance:</span>
                        <button
                          type="button"
                          onClick={() => handleFeedbackClick(result.id, 'up')}
                          className={cn(
                            'px-2 py-1 text-xs border rounded transition-colors',
                            feedback?.feedback === 'up'
                              ? 'bg-green-100 border-green-300 text-green-700'
                              : 'bg-white border-gray-300 text-gray-600 hover:bg-green-50',
                          )}
                          title="Relevant result"
                        >
                          {feedback?.feedback === 'up' ? (
                            <HandThumbUpSolidIcon className="w-3 h-3" />
                          ) : (
                            <HandThumbUpIcon className="w-3 h-3" />
                          )}
                        </button>
                        
                        <button
                          type="button"
                          onClick={() => handleFeedbackClick(result.id, 'down')}
                          className={cn(
                            'px-2 py-1 text-xs border rounded transition-colors',
                            feedback?.feedback === 'down'
                              ? 'bg-red-100 border-red-300 text-red-700'
                              : 'bg-white border-gray-300 text-gray-600 hover:bg-red-50',
                          )}
                          title="Not relevant"
                        >
                          {feedback?.feedback === 'down' ? (
                            <HandThumbDownSolidIcon className="w-3 h-3" />
                          ) : (
                            <HandThumbDownIcon className="w-3 h-3" />
                          )}
                        </button>
                      </div>
                      
                      {/* Comment */}
                      <div>
                        <textarea
                          value={feedback?.comment || ''}
                          onChange={(e) => handleCommentChange(result.id, e.target.value)}
                          className="w-full p-2 border border-gray-300 rounded bg-white text-gray-900 placeholder:text-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-y text-xs"
                          rows={2}
                          placeholder="Optional comment about this result..."
                        />
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
            
            {/* Footer with Global Options and Actions */}
            <div className="p-6 border-t border-gray-300 flex-shrink-0 bg-white">
              {/* Global Options */}
              <div className="space-y-3 mb-6">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={includeContext}
                    onChange={(e) => setIncludeContext(e.target.checked)}
                    className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                  />
                  <span className="text-sm text-gray-700">
                    Include search query and context
                  </span>
                </label>
                
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={includeUserName}
                    onChange={(e) => setIncludeUserName(e.target.checked)}
                    className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                  />
                  <span className="text-sm text-gray-700">
                    Include my username
                  </span>
                </label>
              </div>
              
              {/* Actions */}
              <div className="flex gap-3 justify-end">
                <button 
                  type="button"
                  className="px-4 py-2 border border-gray-300 rounded-lg bg-white text-gray-700 hover:bg-gray-50 transition-colors"
                  onClick={handleCancel}
                >
                  Cancel
                </button>
                <button 
                  type="submit"
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                >
                  Submit Feedback
                </button>
              </div>
            </div>
          </form>
        </div>
      </div>
    </>
  );
};

interface SearchResultsFeedbackProps {
  searchResults: SearchResultItem[];
  searchQuery?: string; // Add search query prop
  onFeedback?: (_feedbackData: SearchResultFeedbackData[], _reorderedResults: SearchResultItem[], _includeContext: boolean, _includeUserName: boolean, _searchQuery?: string) => void;
}

export const SearchResultsFeedback: React.FC<SearchResultsFeedbackProps> = ({
  searchResults,
  searchQuery,
  onFeedback,
}) => {
  const [showPopup, setShowPopup] = useState(false);

  const handleFeedbackSubmit = (
    feedbackData: SearchResultFeedbackData[], 
    reorderedResults: SearchResultItem[],
    includeContext: boolean, 
    includeUserName: boolean,
    searchQuery?: string,
  ) => {
    if (onFeedback) {
      onFeedback(feedbackData, reorderedResults, includeContext, includeUserName, searchQuery);
    }
  };

  const hasResults = searchResults.length > 0;

  return (
    <>
      <button
        onClick={() => setShowPopup(true)}
        disabled={!hasResults}
        className="px-3 py-2 border border-gray-300 rounded-lg bg-white text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center gap-2 text-sm"
        title={hasResults ? 'Provide feedback on search results' : 'No search results to provide feedback on'}
      >
        <ChatBubbleLeftRightIcon className="w-4 h-4" />
        Feedback
      </button>
      
      <SearchResultsFeedbackPopup
        isVisible={showPopup}
        onClose={() => setShowPopup(false)}
        onSubmit={handleFeedbackSubmit}
        searchResults={searchResults}
        searchQuery={searchQuery}
      />
    </>
  );
};